'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';

const STACK_KEY = 'sweetspot_nav_stack';

function readStack(): string[] {
  try {
    const raw = sessionStorage.getItem(STACK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((path): path is string => typeof path === 'string') : [];
  } catch {
    return [];
  }
}

function writeStack(stack: string[]) {
  try {
    sessionStorage.setItem(STACK_KEY, JSON.stringify(stack.slice(-30)));
  } catch {
    /* private mode / quota — animation direction just defaults to forward */
  }
}

/**
 * Slides the page in from the right on forward navigation and from the left
 * when the user goes back (tab bar, back button, iOS edge swipe). Direction is
 * inferred from a per-tab path stack in sessionStorage, so it survives the
 * reload and needs no router internals. Lives in the persistent app layout:
 * nested layouts (inbox lists, settings tabs) must survive route changes.
 *
 * Mobile only (see .page-enter-* in globals.css); desktop renders plainly.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [dir, setDir] = useState<'forward' | 'back' | 'none'>('none');

  useEffect(() => {
    const stack = readStack();
    const last = stack[stack.length - 1];
    if (last === pathname) {
      // Initial render of a restored path — no slide.
      setDir('none');
      return;
    }
    const backIndex = stack.lastIndexOf(pathname);
    // Moving within a section replaces its detail, not the entire workspace.
    const sameSection = last?.split('/')[1] === pathname.split('/')[1];
    if (backIndex >= 0 && backIndex === stack.length - 2) {
      writeStack(stack.slice(0, backIndex + 1));
      setDir(sameSection ? 'none' : 'back');
    } else {
      writeStack([...stack, pathname]);
      setDir(stack.length === 0 || sameSection ? 'none' : 'forward');
    }
  }, [pathname]);

  const cls = dir === 'forward' ? 'page-enter-forward' : dir === 'back' ? 'page-enter-back' : '';
  return (
    <div
      // Page gutters live here, not on the scroller: sticky offsets are measured
      // from a scroll container's content edge, so padding on `main` shifted
      // every sticky bar down by the padding (measured 64px → 144px). Pages
      // that go edge-to-edge cancel these with -mx-4/-mt-4 as before.
      // No `min-h-0` here: as a flex item it would let the page overflow this
      // box, and a box's bottom padding is painted at *its* bottom edge — not
      // after the overflow — so the last row scrolled under the tab bar.
      // Full-height pages get min-height back via `:has(.app-fullscreen)`.
      className={`app-content flex flex-1 flex-col min-w-0 px-4 pt-[calc(var(--app-top-inset)+1rem)] pb-[calc(var(--app-bottom-inset)+1rem)] md:p-6 ${cls}`}
      // A finished animation with fill-mode:both keeps a transform on the
      // element, which turns it into the containing block for every
      // position:fixed descendant (sheets, sticky bars). Drop it once done.
      onAnimationEnd={() => setDir('none')}
    >
      {children}
    </div>
  );
}
